import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Global voice recording cleanup hook
 * Automatically stops all voice recording when navigating between pages
 */
export const useVoiceRecordingCleanup = () => {
  const location = useLocation();
  const previousLocation = useRef<string>('');

  useEffect(() => {
    // Only cleanup if we've actually navigated to a different page
    if (previousLocation.current && previousLocation.current !== location.pathname) {
      console.log('Navigation detected, cleaning up voice recording...');
      
      // Stop all speech recognition instances
      if (typeof window !== 'undefined' && 'SpeechRecognition' in window) {
        // Get all speech recognition instances (if any are stored globally)
        const globalRecognition = (window as any).globalSpeechRecognition;
        if (globalRecognition && globalRecognition.stop) {
          try {
            globalRecognition.stop();
            console.log('Stopped global speech recognition');
          } catch (error) {
            console.log('Global speech recognition was not running');
          }
        }
      }

      // Stop all media recorder instances
      if (typeof window !== 'undefined' && 'MediaRecorder' in window) {
        const globalMediaRecorder = (window as any).globalMediaRecorder;
        if (globalMediaRecorder && globalMediaRecorder.state === 'recording') {
          try {
            globalMediaRecorder.stop();
            console.log('Stopped global media recorder');
          } catch (error) {
            console.log('Global media recorder was not running');
          }
        }
      }

      // Stop all audio tracks
      if (typeof navigator !== 'undefined' && navigator.mediaDevices) {
        navigator.mediaDevices.getUserMedia({ audio: true })
          .then(stream => {
            stream.getTracks().forEach(track => {
              track.stop();
              console.log('Stopped audio track');
            });
          })
          .catch(() => {
            // No active audio tracks
          });
      }

      // Dispatch a custom event to notify all components to stop recording
      const cleanupEvent = new CustomEvent('voice-recording-cleanup', {
        detail: { reason: 'navigation', from: previousLocation.current, to: location.pathname }
      });
      window.dispatchEvent(cleanupEvent);
    }

    // Update previous location
    previousLocation.current = location.pathname;
  }, [location.pathname]);
};

export default useVoiceRecordingCleanup;
